import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmt } from "@/lib/format";
import TokenCertificate from "@/components/app/TokenCertificate";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Tag, X } from "lucide-react";

export default function MyTokens() {
  const { user } = useAuth();
  const [bricks, setBricks] = useState([]);
  const [selected, setSelected] = useState(null);
  const [resalePrice, setResalePrice] = useState("");

  const load = () =>
    api.get("/my/bricks").then(({ data }) => setBricks(data)).catch(() => {});

  useEffect(() => {
    load();
  }, []);

  const list = async (brickId) => {
    try {
      await api.post(`/bricks/${brickId}/list`, { resale_price: parseFloat(resalePrice) });
      toast.success("Listed for resale");
      setSelected(null);
      setResalePrice("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const unlist = async (brickId) => {
    try {
      await api.delete(`/bricks/${brickId}/list`);
      toast.success("Unlisted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
      <div className="overline">Ownership</div>
      <h1 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-white mt-2 mb-8">
        My Tokens
      </h1>

      {bricks.length === 0 ? (
        <div className="bp-surface rounded-2xl p-12 text-center">
          <div className="text-gray-400">You don't own any tokens yet</div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
          {bricks.map((b) => (
            <div key={b.id} className="relative">
              <TokenCertificate brick={b} property={b.property} ownerName={user?.name} />
              <div className="mt-3 flex gap-2">
                {b.listed_for_resale ? (
                  <>
                    <div className="flex-1 bp-surface rounded-full h-9 flex items-center justify-center text-[11px] text-blue-400 gap-2">
                      <Tag className="w-3 h-3" /> Listed {fmt(b.resale_price, { decimals: true })}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-full"
                      onClick={() => unlist(b.id)}
                      data-testid={`unlist-${b.id}`}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </>
                ) : (
                  <Button
                    data-testid={`list-resale-${b.id}`}
                    size="sm"
                    className="flex-1 btn-gold-outline rounded-full h-9 text-xs"
                    onClick={() => {
                      setSelected(b);
                      setResalePrice(String(b.purchase_price || 0));
                    }}
                  >
                    <Tag className="w-3 h-3 mr-1" /> List for resale
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-[#0B1120] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-heading">List brick for resale</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-gray-400">{selected?.token_id}</div>
            <div>
              <Label className="text-gray-300">Resale price (NGN)</Label>
              <Input
                data-testid="resale-price-input"
                type="number"
                value={resalePrice}
                onChange={(e) => setResalePrice(e.target.value)}
                className="mt-1 bg-[#050914] border-white/10 focus:border-[#D4AF37]"
              />
              <div className="text-[11px] text-gray-500 mt-1">
                You paid {selected?.purchase_price ? fmt(selected.purchase_price, { decimals: true }) : "—"}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelected(null)}>
              Cancel
            </Button>
            <Button className="btn-gold rounded-full" onClick={() => list(selected.id)} data-testid="confirm-list-btn">
              List
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
